// src/hooks/useCoopMembership.js
// Hook for cooperative membership operations

import { useState, useCallback } from "react";
import { db } from "@/lib/firebase";
import { 
  doc, 
  setDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp 
} from "firebase/firestore";
import { 
  COOP_STATUS, 
  COOP_CLASS, 
  validateCoopMembershipApplication,
  createInitialCoopMembership,
  validateCoopMembership
} from "@/lib/coopMembers.js";
import { syncBasicProfile } from "../lib/syncProfileData";
import { generateMemberId } from "@/lib/memberId.js";

export default function useCoopMembership() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Submit a new cooperative membership application
   */
  const submitApplication = useCallback(async (applicationData) => {
    setLoading(true);
    setError(null);

    try {
      const validation = validateCoopMembershipApplication(applicationData);
      if (!validation.success) {
        throw new Error(`Validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
      }

      const membershipData = {
        uid: applicationData.uid,
        class: applicationData.class,
        status: COOP_STATUS.PENDING,
        verifiedByAdmin: false,
        kycDocs: applicationData.kycDocs,
        dues: applicationData.dues,
        phone: applicationData.phone,
        address: applicationData.address,
        occupation: applicationData.occupation,
        businessType: applicationData.businessType,
        annualIncome: applicationData.annualIncome,
        emergencyContact: applicationData.emergencyContact || {
          name: '',
          phone: '',
          relationship: '',
          address: ''
        },
        applicationNotes: applicationData.applicationNotes || '',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await setDoc(doc(db, "members", applicationData.uid), membershipData);

      return { success: true, data: membershipData };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update membership status (admin only)
   */
  const updateMembershipStatus = async (uid, newStatus, adminUid, adminNotes = '') => {
    setLoading(true);
    setError(null);

    try {
      if (!adminUid) {
        throw new Error('Admin user not authenticated');
      }

      const updateData = {
        status: newStatus,
        verifiedByAdmin: newStatus === COOP_STATUS.APPROVED,
        adminNotes: adminNotes || '',
        updatedAt: serverTimestamp(),
        verifiedBy: adminUid,
        verifiedAt: serverTimestamp()
      };

      // If approving, generate and assign member ID
      if (newStatus === COOP_STATUS.APPROVED) {
        const memberId = await generateMemberId(db);
        updateData.memberId = memberId;
        updateData.approvedAt = serverTimestamp();
        

      }

      await updateDoc(doc(db, "members", uid), updateData);

      return { 
        success: true, 
        memberId: updateData.memberId || null 
      };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update membership data
   */
  const updateMembership = useCallback(async (uid, updateData) => {
    setLoading(true);
    setError(null);

    try {
      if (!uid) {
        throw new Error('User ID is required');
      }

      const updatePayload = {
        ...updateData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(doc(db, "members", uid), updatePayload);

      // If this update includes profile data, sync it to users collection
      const profileFields = ['firstName', 'middleName', 'lastName', 'email', 'phone', 'address', 'profilePhotoURL'];
      const hasProfileData = profileFields.some(field => updateData[field] !== undefined);
      
      if (hasProfileData) {
        await syncBasicProfile(uid, updateData);
      }

      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Assign member ID (admin only)
   */
  const assignMemberId = useCallback(async (uid, memberId) => {
    setLoading(true);
    setError(null);

    try {
      if (!memberId || memberId.trim().length === 0) {
        throw new Error('Member ID is required');
      }

      // Check if member ID is already in use
      const existingMember = await getDocs(
        query(collection(db, "members"), where("memberId", "==", memberId.trim()), limit(1))
      );
      
      if (!existingMember.empty && existingMember.docs[0].id !== uid) {
        throw new Error('Member ID is already in use');
      }

      await updateDoc(doc(db, "members", uid), {
        memberId: memberId.trim(),
        updatedAt: serverTimestamp()
      });

      // Also sync memberId to users collection for consistency
      await syncBasicProfile(uid, { memberId: memberId.trim() });
      
      return { success: true };
      
    } catch (err) {
      const errorMessage = err?.message || 'Failed to assign member ID';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update KYC documents
   */
  const updateKycDocuments = useCallback(async (uid, kycDocs) => {
    setLoading(true);
    setError(null);

    try {
      await updateDoc(doc(db, "members", uid), {
        kycDocs,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
      
    } catch (err) {
      const errorMessage = err?.message || 'Failed to update KYC documents';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update dues information
   */
  const updateDues = useCallback(async (uid, dues) => {
    setLoading(true);
    setError(null);

    try {
      await updateDoc(doc(db, "members", uid), {
        dues,
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
      
    } catch (err) {
      const errorMessage = err?.message || 'Failed to update dues';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Admin: Get pending applications
  const getPendingApplications = useCallback(async () => {
    try {
      const q = query(
        collection(db, "members"),
        where("status", "==", COOP_STATUS.PENDING),
        orderBy("createdAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      const applications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      return { success: true, data: applications };
    } catch (err) {
      
      return { success: false, error: err.message };
    }
  }, []);

  // Admin: Get all approved members
  const getApprovedMembers = useCallback(async () => {
    try {
      const q = query(
        collection(db, "members"),
        where("status", "==", COOP_STATUS.APPROVED),
        orderBy("createdAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      return { success: true, data: members };
    } catch (err) {
      
      return { success: false, error: err.message };
    }
  }, []);

  // Admin: Search members with filters
  const searchMembers = useCallback(async (filters = {}) => {
    try {
      let q = collection(db, "members");
      
      // Apply filters
      if (filters.status) {
        q = query(q, where("status", "==", filters.status));
      }
      if (filters.class) {
        q = query(q, where("class", "==", filters.class));
      }
      
      // Always order by creation date
      q = query(q, orderBy("createdAt", "desc"));
      
      const snapshot = await getDocs(q);
      const members = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      return { success: true, data: members };
    } catch (err) {
      
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Clear any errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    loading,
    error,
    
    // Actions
    submitApplication,
    updateMembershipStatus,
    updateMembership,
    assignMemberId,
    updateKycDocuments,
    updateDues,
    
    // Queries
    getPendingApplications,
    getApprovedMembers,
    searchMembers,
    
    // Utilities
    clearError
  };
}
